/* eslint-disable eqeqeq */
import React, { useState, useEffect } from "react";
import api from "../api";
import { toast } from 'react-toastify';
import { FaFileDownload, FaInfoCircle } from "react-icons/fa"; 

const UploadBeneficiaryListPage = () => {
    // --- AUTOMATIC ID FETCHING ---
    const storedUserStr = localStorage.getItem("currentUser");
    const currentUser = storedUserStr ? JSON.parse(storedUserStr) : null;
    
    // 1. Get the selected project ID from storage
    let selectedProjectId = localStorage.getItem("selectedProjectId");

    // 2. Find the active project object within the user's project list
    let activeProject = null;
    if (currentUser && currentUser.projects && currentUser.projects.length > 0) {
        activeProject = currentUser.projects.find(p => p.projectId == selectedProjectId);
        if (!activeProject) {
            activeProject = currentUser.projects[0];
            localStorage.setItem("selectedProjectId", activeProject.projectId);
        }
    }

    const projectId = activeProject ? activeProject.projectId : "";
    const departmentId = activeProject ? activeProject.departmentId : "";

    const [files, setFiles] = useState([]);
    const [voucherPoints, setVoucherPoints] = useState("");
    const [validityStart, setValidityStart] = useState("");
    const [validityEnd, setValidityEnd] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    
    const [uploadResponseMsg, setUploadResponseMsg] = useState("");
    const [error, setError] = useState(""); 

    // --- ✅ UPDATED: FETCH & PRE-FILL PROJECT DETAILS ---
    useEffect(() => {
        const fetchProjectDetails = async () => {
            if (!projectId) return;
            try {
                const res = await api.get(`/vres/projects/${projectId}`);
                const data = res.data;
                
                console.log("Project Data for Prefill:", data); // Check console to see what API returns

                // 1. Map Points
                // Checks for 'voucherPoints' (DTO) OR 'voucher_points' (Entity fallback)
                const points = data.voucherPoints || data.voucher_points;
                if (points) setVoucherPoints(points);

                // 2. Map Dates (Helper to safely format YYYY-MM-DD)
                const formatDate = (dateVal) => {
                    if (!dateVal) return "";
                    // If it's "2026-02-02T00:00:00", split at T. If just "2026-02-02", it works too.
                    return dateVal.toString().split('T')[0];
                };

                const start = data.validFrom || data.voucher_valid_from;
                if (start) setValidityStart(formatDate(start));

                const end = data.validTill || data.voucher_valid_till;
                if (end) setValidityEnd(formatDate(end));

            } catch (err) {
                console.error("Failed to fetch project details for pre-filling", err);
            }
        };

        fetchProjectDetails();
    }, [projectId]);

    useEffect(() => {
        if (currentUser && (!projectId || !departmentId)) {
            const missingInfoMessage = "Project or Department information is missing. Please log out and select a project again.";
            setError(missingInfoMessage);
            toast.error(missingInfoMessage);
        }
    }, [projectId, departmentId, currentUser]);

    const handleFileChange = (e) => {
        setFiles(Array.from(e.target.files));
        setUploadResponseMsg("");
        setError("");
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError("");
        setUploadResponseMsg("");

        if (!projectId || !departmentId) {
            toast.error("Cannot submit. Project/Dept missing.");
            return;
        }
        if (files.length === 0) {
            toast.error("Please select at least one Excel file.");
            return;
        }
        if (!voucherPoints || !validityStart || !validityEnd) {
            toast.error("Please fill in Voucher Points and Validity Dates.");
            return;
        }
        if (validityStart && validityEnd && validityEnd < validityStart) {
            toast.error("Validity end date cannot be before start date.");
            return;
        }

        const allowedTypes = [".xls", ".xlsx"];
        const invalidFiles = files.filter(f => !allowedTypes.some(ext => f.name.endsWith(ext)));
        if (invalidFiles.length > 0) {
            toast.error("Only Excel files (.xls, .xlsx) are allowed.");
            return;
        }

        setIsLoading(true);

        const formData = new FormData();
        files.forEach((file) => formData.append("beneficiary_files", file));

        const params = {
            department_id: departmentId,
            voucher_points: voucherPoints,
            validity_start: validityStart,
            validity_end: validityEnd
        };

        try {
            const response = await api.post(
                `/vres/projects/${projectId}/beneficiaries/upload`,
                formData,
                {
                    headers: { "Content-Type": "multipart/form-data" },
                    params: params 
                }
            );

            setUploadResponseMsg(response.data.message || `Successfully submitted.`);
            toast.success("Upload processed.");
            setFiles([]);
            const fileInput = document.getElementById("beneficiary_files");
            if (fileInput) fileInput.value = "";

        } catch (err) {
            console.error("Upload error:", err);
            const msg = err.response?.data?.message || err.message || "Upload failed.";
            setError(msg);
            toast.error(msg);
        } finally {
            setIsLoading(false);
        }
    };

    const handleCancel = () => {
        setFiles([]);
        setUploadResponseMsg("");
        setError("");
        const fileInput = document.getElementById("beneficiary_files");
        if (fileInput) fileInput.value = "";
        
        // Optional: Re-fetch original values to reset form
        // fetchProjectDetails(); 
    };

    const renderStatusMessage = () => {
        if (!uploadResponseMsg) return null;
        const parts = uploadResponseMsg.split(" || ");
        const successPart = parts[0];
        let errorHeader = null;
        let errorList = [];

        if (parts.length > 1) {
            const errorParts = parts[1].split("::");
            errorHeader = errorParts[0];
            errorList = errorParts.slice(1);
        }

        return (
            <div style={{ marginTop: '20px', padding: '15px', backgroundColor: '#f8f9fa', borderRadius: '5px', border: '1px solid #dee2e6' }}>
                <div style={{ color: '#28a745', fontWeight: 'bold', marginBottom: '10px' }}>
                    <i className="bi bi-check-circle-fill me-2"></i>{successPart}
                </div>
                {errorHeader && (
                    <div style={{ color: '#dc3545', fontSize: '0.95em', borderTop: '1px solid #e9ecef', paddingTop: '10px' }}>
                        <strong>{errorHeader}</strong>
                        <div style={{ marginTop: '5px', paddingLeft: '10px' }}>
                            {errorList.map((err, index) => <div key={index} style={{ marginBottom: '2px' }}>{err}</div>)}
                        </div>
                    </div>
                )}
            </div>
        );
    };

    const PageStyleBlock = () => (
        <style>{`
            .ub-input:focus { border-color: #20c997 !important; box-shadow: 0 0 0 3px rgba(32, 201, 151, 0.1) !important; outline: none; }
            .ub-btn:hover:not(:disabled) { opacity: 0.9; }
            .ub-btn:disabled { opacity: 0.6; cursor: not-allowed; }
        `}</style>
    );

    return (
        <div style={styles.formWrapper}>
            <PageStyleBlock />
            <div style={styles.formCard}>
                <h2 style={styles.formTitle}>Upload Beneficiary List for Project {projectId}</h2>

                <div style={styles.templateSection}>
                    <div style={styles.templateInfo}>
                        <FaInfoCircle style={{ marginRight: '8px', color: '#0056b3' }} />
                        <span>Please upload data using the standard format.</span>
                    </div>
                    <a href="/beneficiary_template.xlsx" download="Beneficiary_Template.xlsx" style={styles.downloadLink}>
                        <FaFileDownload style={{ marginRight: '8px' }} /> Download Sample
                    </a>
                </div>

                <form onSubmit={handleSubmit} onReset={handleCancel}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' }}>
                        <div>
                            <label style={styles.formLabel}>Voucher Points</label>
                            <input type="number" className="ub-input" style={styles.formControl}
                                value={voucherPoints} onChange={(e) => setVoucherPoints(e.target.value)}
                                required min="1" placeholder="e.g. 500"
                            />
                        </div>
                        <div>
                            <label style={styles.formLabel}>Validity Start Date</label>
                            <input type="date" className="ub-input" style={styles.formControl}
                                value={validityStart} onChange={(e) => setValidityStart(e.target.value)} required
                            />
                        </div>
                        <div>
                            <label style={styles.formLabel}>Validity End Date</label>
                            <input type="date" className="ub-input" style={styles.formControl}
                                value={validityEnd} onChange={(e) => setValidityEnd(e.target.value)} required min={validityStart}
                            />
                        </div>
                    </div>

                    <div style={{ marginBottom: '20px' }}>
                        <label htmlFor="beneficiary_files" style={styles.formLabel}>Select Beneficiary List File(s)</label>
                        <input type="file" id="beneficiary_files" className="ub-input"
                            style={{ ...styles.formControl, padding: '10px' }} 
                            accept=".xls,.xlsx" multiple required onChange={handleFileChange}
                            disabled={isLoading || !projectId || !departmentId}
                        />
                    </div>

                    {isLoading && <p style={{ color: "#007bff", marginTop: "15px", textAlign: 'center' }}>Uploading, please wait...</p>}
                    {error && <p style={{ color: "red", marginTop: "15px", textAlign: 'center' }}>{error}</p>}
                    {renderStatusMessage()}

                    <div style={styles.btnGroup}>
                        <button type="reset" className="ub-btn" style={{ ...styles.btn, ...styles.btnSecondary }} disabled={isLoading}>Cancel</button>
                        <button type="submit" className="ub-btn" style={{ ...styles.btn, ...styles.btnPrimary }} disabled={isLoading || !projectId || !departmentId}>
                            {isLoading ? "Uploading..." : "Submit"}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

const styles = {
    formWrapper: { display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%', minHeight: 'calc(100vh - 80px)', padding: '20px', boxSizing: 'border-box', fontFamily: "'Segoe UI', Roboto, sans-serif", backgroundColor: '#f0fdf9' },
    formCard: { backgroundColor: 'white', width: '100%', maxWidth: '800px', padding: '30px', borderRadius: '12px', boxShadow: '0 10px 30px rgba(0,0,0,0.08)', borderTop: '5px solid #20c997' },
    formTitle: { textAlign: 'center', marginBottom: '20px', fontSize: '1.4rem', color: '#007bff', fontWeight: '700', marginTop: 0 },
    templateSection: { backgroundColor: "#eef6fc", border: "1px solid #cce5ff", borderRadius: "6px", padding: "12px 15px", marginBottom: "20px", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "10px" },
    templateInfo: { display: "flex", alignItems: "center", color: "#004085", fontSize: "0.9rem" },
    downloadLink: { display: "flex", alignItems: "center", textDecoration: "none", backgroundColor: "#fff", color: "#007bff", padding: "6px 12px", borderRadius: "4px", border: "1px solid #007bff", fontWeight: "600", fontSize: "0.85rem", transition: "background-color 0.2s" },
    formLabel: { display: 'block', marginBottom: '6px', fontWeight: '600', color: '#2d3436', fontSize: '0.9rem' },
    formControl: { width: '100%', padding: '10px 12px', border: '1px solid #dcdcdc', borderRadius: '6px', fontSize: '0.9rem', transition: 'all 0.3s', outline: 'none', boxSizing: 'border-box', color: '#2d3436', backgroundColor: '#fff' },
    btnGroup: { display: 'flex', gap: '15px', marginTop: '25px', justifyContent: 'center' },
    btn: { padding: '10px 30px', borderRadius: '6px', fontSize: '0.95rem', fontWeight: '600', cursor: 'pointer', border: 'none', minWidth: '120px', transition: 'opacity 0.2s' },
    btnPrimary: { backgroundColor: '#007bff', color: 'white' },
    btnSecondary: { backgroundColor: '#6c757d', color: 'white' },
};
export default UploadBeneficiaryListPage;